package com.gastoncantera.intercom.auth

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.keyframes
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.launch

private const val HOLD_TO_ACCEPT_MILLIS = 3_000
private const val MAX_WELCOME_RESET_REQUESTS = 3
private val BahiaBlue = Color(0xFF0D5DBD)

@Composable
fun WelcomeDialog(
    authRepository: AuthRepository,
    onAccepted: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var email by remember { mutableStateOf("") }
    var resetSentThisOpening by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var info by remember { mutableStateOf<String?>(null) }
    var emphasizeHoldHint by remember { mutableStateOf(false) }

    Dialog(
        onDismissRequest = {},
        properties = DialogProperties(
            dismissOnBackPress = false,
            dismissOnClickOutside = false,
        ),
    ) {
        // Dialog compone en su propia ventana: el LocalFocusManager de afuera no ve este campo.
        val focusManager = LocalFocusManager.current
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.large,
            color = Color.White,
            tonalElevation = 0.dp,
        ) {
            Column(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .pointerInput(Unit) {
                        detectTapGestures(onTap = { focusManager.clearFocus() })
                    }
                    .padding(24.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Text(
                    text = "Bienvenido a Timbre Bahía",
                    style = MaterialTheme.typography.headlineSmall,
                )
                Text(
                    "Esta app te permite recibir en el teléfono las llamadas del portero " +
                        "de tu edificio o propiedad, con audio y video.",
                )
                Text(
                    "Las cuentas son creadas por el administrador del consorcio o de la propiedad. " +
                        "Para ingresar por primera vez, tenés que restablecer tu contraseña.",
                )
                Text(
                    text = "Tip: si ingresás mal la contraseña 3 veces, también vas a poder " +
                        "restablecerla desde la pantalla de inicio de sesión.",
                    style = MaterialTheme.typography.bodySmall,
                )

                if (
                    !resetSentThisOpening &&
                    WelcomeStore.resetRequestCount < MAX_WELCOME_RESET_REQUESTS
                ) {
                    Text(
                        text = "Podés pedir ahora el email de restablecimiento (opcional). " +
                            "Revisá también Spam o Correo no deseado.",
                        style = MaterialTheme.typography.bodyMedium,
                    )
                    OutlinedTextField(
                        value = email,
                        onValueChange = { email = it },
                        label = { Text("Email") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Email,
                            imeAction = ImeAction.Done,
                        ),
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Button(
                        onClick = {
                            focusManager.clearFocus()
                            error = null
                            info = null
                            if (!email.isValidEmail()) {
                                error = if (email.isBlank()) {
                                    "Ingresá tu email para restablecer la contraseña."
                                } else {
                                    "El email no es válido."
                                }
                            } else {
                                loading = true
                                scope.launch {
                                    try {
                                        authRepository.sendPasswordResetEmail(email)
                                        WelcomeStore.resetRequestCount =
                                            (WelcomeStore.resetRequestCount + 1)
                                                .coerceAtMost(MAX_WELCOME_RESET_REQUESTS)
                                        resetSentThisOpening = true
                                        info = "Si existe una cuenta con ese email, te enviamos " +
                                            "instrucciones para restablecer la contraseña. " +
                                            "El mensaje puede llegar a Spam o Correo no deseado."
                                    } catch (e: Throwable) {
                                        error = resetErrorMessage(e)
                                    } finally {
                                        loading = false
                                    }
                                }
                            }
                        },
                        enabled = !loading,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = BahiaBlue,
                            contentColor = Color.White,
                        ),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        if (loading) {
                            CircularProgressIndicator(color = Color.White)
                        } else {
                            Text("Enviar email de restablecimiento")
                        }
                    }
                }

                error?.let {
                    Text(
                        text = it,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }
                info?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }

                Text(
                    text = "Para continuar, mantené presionado Entendido durante 3 segundos.",
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = if (emphasizeHoldHint) FontWeight.Bold else FontWeight.Normal,
                )
                HoldToConfirmButton(
                    onConfirmed = onAccepted,
                    onReleasedEarly = { emphasizeHoldHint = true },
                )
            }
        }
    }
}

@Composable
private fun HoldToConfirmButton(
    onConfirmed: () -> Unit,
    onReleasedEarly: () -> Unit,
) {
    val progress = remember { Animatable(0f) }
    val shake = remember { Animatable(0f) }
    val scope = rememberCoroutineScope()
    val haptics = LocalHapticFeedback.current

    Surface(
        modifier = Modifier
            .offset(x = shake.value.dp)
            .fillMaxWidth()
            .height(52.dp)
            .semantics {
                contentDescription = "Entendido. Mantené presionado durante 3 segundos."
            }
            .pointerInput(onConfirmed) {
                detectTapGestures(
                    onPress = {
                        var confirmed = false
                        haptics.performHapticFeedback(HapticFeedbackType.VirtualKey)
                        scope.launch { shake.snapTo(0f) }
                        val animation = scope.launch {
                            progress.stop()
                            progress.snapTo(0f)
                            progress.animateTo(
                                targetValue = 1f,
                                animationSpec = tween(
                                    durationMillis = HOLD_TO_ACCEPT_MILLIS,
                                    easing = LinearEasing,
                                ),
                            )
                            confirmed = true
                            haptics.performHapticFeedback(HapticFeedbackType.VirtualKey)
                            onConfirmed()
                        }
                        tryAwaitRelease()
                        if (!confirmed) {
                            animation.cancel()
                            haptics.performHapticFeedback(HapticFeedbackType.Reject)
                            onReleasedEarly()
                            scope.launch { progress.snapTo(0f) }
                            scope.launch {
                                shake.animateTo(
                                    targetValue = 0f,
                                    animationSpec = keyframes {
                                        durationMillis = 300
                                        -8f at 40
                                        8f at 80
                                        -6f at 130
                                        6f at 180
                                        -3f at 230
                                    },
                                )
                            }
                        }
                    },
                )
            },
        color = BahiaBlue,
        contentColor = Color.White,
        shape = MaterialTheme.shapes.medium,
    ) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center,
        ) {
            Spacer(
                modifier = Modifier
                    .align(Alignment.CenterStart)
                    .fillMaxHeight()
                    .fillMaxWidth(progress.value)
                    .background(Color.White.copy(alpha = 0.45f)),
            )
            Text("Entendido")
        }
    }
}
